import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Text,
  Link,
  PopoverContent,
  PopoverHeader,
  PopoverAnchor,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  VStack,
  Popover,
  useDisclosure,
  Box,
} from '@chakra-ui/react';
import { LoadingSpinner } from '@gamma/ui/progress';
import { NotesPopoverPagination } from './NotesPopoverPagination';
import { useGetCommentsByEntityNamePaginated } from '../../queries';
import type { EntityComment } from '../../queries/types';
import { i18n } from '@investigator/shared/localization';
import { NoteItem } from './NoteItem';
import { sortCommentsWithIPMentionsFirst } from '../../utils/sortCommentsWithIPMentionsFirst';

const NOTES_PER_PAGE = 5;
// Large fallback number in case noteCount is 0 or unreliable
const MAX_FETCH_SIZE = 100;

const { title, errorMsgPrefix, noNotes, unknownError } =
  i18n.pages.entities.notesPopover;

interface NotesPopoverState {
  pageIndex: number;
  pageSize: number;
}

interface NotesPopoverProps {
  noteCount: number;
  entityName: string;
  tenantId: string;
  start?: number;
  end?: number;
}

export const NotesPopover = ({
  noteCount,
  entityName,
  tenantId,
  start,
  end,
}: NotesPopoverProps): JSX.Element => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [state, setState] = useState<NotesPopoverState>({
    pageIndex: 0,
    pageSize: NOTES_PER_PAGE,
  });

  // Fetch all notes at once - use noteCount if available, otherwise use large fallback
  const fetchSize = useMemo(() => {
    return noteCount || MAX_FETCH_SIZE;
  }, [noteCount]);

  const { data, loading, error } = useGetCommentsByEntityNamePaginated({
    skip: !isOpen || !entityName || !start || !end || !tenantId,
    variables: {
      entity_name: entityName,
      tenant: tenantId,
      offset: 0,
      size: fetchSize, // Fetch all notes at once
      sort: { field: 'created_at', direction: 'desc' },
      start_ts: start ?? 0,
      end_ts: end ?? 0,
    },
  });

  const allComments = useMemo(
    () => data?.getCommentsByEntityNamePaginated?.comments || [],
    [data?.getCommentsByEntityNamePaginated?.comments],
  );

  // Sort all comments: entity mentions first, then by date DESC
  const sortedAllComments = useMemo(() => {
    if (!allComments.length || !entityName) {
      return allComments;
    }
    return sortCommentsWithIPMentionsFirst(allComments, entityName);
  }, [allComments, entityName]);

  // Calculate pagination for client-side pagination
  const pageCount = useMemo(() => {
    const calculatedPageCount = Math.ceil(
      sortedAllComments.length / state.pageSize,
    );
    return calculatedPageCount > 0 ? calculatedPageCount : 1;
  }, [sortedAllComments.length, state.pageSize]);

  // Get paginated slice of sorted comments for display
  const paginatedComments = useMemo(() => {
    const { pageIndex, pageSize } = state;
    const startIndex = pageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedAllComments.slice(startIndex, endIndex);
  }, [sortedAllComments, state]);

  const isInitialLoad = loading && !error;

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setState({ pageSize: newPageSize, pageIndex: 0 });
  }, []);

  const handlePageIndexChange = useCallback((newPageIndex: number) => {
    setState((prev) => ({
      pageIndex: newPageIndex,
      pageSize: prev.pageSize,
    }));
  }, []);

  const handleToggleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggle();
    },
    [onToggle],
  );

  useEffect(() => {
    if (!isOpen) {
      setState({
        pageIndex: 0,
        pageSize: NOTES_PER_PAGE,
      } as NotesPopoverState);
    }
  }, [isOpen]);

  return (
    <Popover
      placement="auto"
      isOpen={isOpen}
      onClose={onClose}
      strategy="fixed"
    >
      <PopoverAnchor>
        <Link
          aria-label={`${noteCount} notes`}
          color="text.link"
          cursor="pointer"
          onClick={handleToggleClick}
        >
          {noteCount}
        </Link>
      </PopoverAnchor>
      <PopoverContent
        width="480px"
        zIndex="popover"
        maxHeight="600px"
        _focus={{ outline: 'none', boxShadow: 'none' }}
        _focusVisible={{ outline: 'none', boxShadow: 'none' }}
        onClick={(e) => e.stopPropagation()}
      >
        <PopoverArrow />
        <PopoverCloseButton w={5} h={5} fontSize="xs" right={2} top={2} />
        <PopoverHeader fontWeight="bold" flexShrink={0}>
          {`${entityName} | ${title}`}
        </PopoverHeader>
        <PopoverBody
          flex="1"
          overflowY="auto"
          overflowX="hidden"
          position="relative"
          minHeight="200px" // Prevent layout shift during initial load
          // Only use flex centering for initial load spinner
          display={isInitialLoad ? 'flex' : 'block'}
          alignItems={isInitialLoad ? 'center' : undefined}
          justifyContent={isInitialLoad ? 'center' : undefined}
        >
          {/* Error message when do not have data */}
          {error && sortedAllComments.length === 0 && (
            <Box py={4} px={4}>
              <Text
                color="text.error"
                textAlign="center"
                wordBreak="break-word"
                overflowWrap="break-word"
                whiteSpace="normal"
              >
                {errorMsgPrefix} {error?.message || unknownError}
              </Text>
            </Box>
          )}

          {isInitialLoad && <LoadingSpinner />}

          {/* Notes list */}
          {!isInitialLoad && (
            <VStack align="stretch" spacing={3} width="100%">
              {paginatedComments.length === 0 && !loading && !error ? (
                <Text color="text.secondary" py={4} textAlign="center">
                  {noNotes}
                </Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  {paginatedComments.map((note: EntityComment) => (
                    <NoteItem
                      entityName={entityName}
                      key={note.comment_id}
                      note={note}
                      start={start}
                      end={end}
                    />
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </PopoverBody>

        {/* Pagination controls */}
        {!isInitialLoad && sortedAllComments.length > 0 && (
          <Box
            pt={2}
            pb={2}
            px={4}
            flexShrink={0}
            bg="layer.1"
            sx={{
              '& > *': {
                borderTop: 'none !important', // hide the top border of the DataTablePagination component to match product design.
              },
            }}
          >
            <NotesPopoverPagination
              pageSize={state.pageSize}
              pageIndex={state.pageIndex}
              pageCount={pageCount || 1}
              itemCount={sortedAllComments.length}
              onPageSizeChange={handlePageSizeChange}
              onPageIndexChange={handlePageIndexChange}
            />
          </Box>
        )}
      </PopoverContent>
    </Popover>
  );
};
